const ANGLE_RE = /[<>]/g;
const BRACKET_MAP: Record<string, string> = {
  '<': '&lt;',
  '>': '&gt;'
};

export const escapeHtmlText = (text: string): string =>
  text.replace(ANGLE_RE, (ch) => BRACKET_MAP[ch] ?? ch);

const ALLOWED_TAGS = new Set([
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'del',
  'ul',
  'ol',
  'li',
  'br',
  'div',
  'p',
  'span'
]);

const VOID_TAGS = new Set(['br']);

const serializeNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtmlText(node.textContent ?? '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (!ALLOWED_TAGS.has(tag)) {
    return Array.from(el.childNodes).map(serializeNode).join('');
  }

  // Void elements have no children/closing tag; a stray </br> is parsed by
  // browsers as an implicit extra <br>, doubling breaks on every round trip.
  if (VOID_TAGS.has(tag)) return `<${tag}>`;

  const inner = Array.from(el.childNodes).map(serializeNode).join('');
  return `<${tag}>${inner}</${tag}>`;
};
export const serializeEditorContent = (editor: HTMLElement): string =>
  Array.from(editor.childNodes).map(serializeNode).join('');

export const setEditorHtml = (editor: HTMLElement, html: string): void => {
  editor.innerHTML = html;
};
const BLOCK_WRAPPER_TAGS = new Set(['div', 'p']);

const replaceParagraphsWithDivs = (container: HTMLElement): void => {
  container.querySelectorAll('p').forEach((paragraph) => {
    const div = document.createElement('div');
    div.innerHTML = paragraph.innerHTML;
    paragraph.replaceWith(div);
  });
};

const unwrapSingleBlockWrapper = (container: HTMLElement): void => {
  while (
    container.childNodes.length === 1 &&
    container.firstChild?.nodeType === Node.ELEMENT_NODE
  ) {
    const child = container.firstChild as HTMLElement;
    if (!BLOCK_WRAPPER_TAGS.has(child.tagName.toLowerCase())) break;
    container.innerHTML = child.innerHTML;
  }
};

export const canonicalizeRichTextHtml = (html: string): string => {
  if (!html.trim()) return '';

  const container = document.createElement('div');
  container.innerHTML = html;
  replaceParagraphsWithDivs(container);
  unwrapSingleBlockWrapper(container);

  const plainText = (container.textContent ?? '').trim();
  if (!plainText) return '';

  return serializeEditorContent(container);
};

export const getPlainTextFromClipboard = (
  clipboardData: DataTransfer
): string => clipboardData.getData('text/plain');
